import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  useContext,
  useState,
  useEffect,
  SyntheticEvent,
} from 'react'
import { useSelector } from '@xstate/react'
import { LmnContext } from 'src/components/Dialogs/Lmn/LmnDialog'
import { getCustomerInfo, getLmnConditions, getLmnContraindications } from 'src/helpers/lmnHelpers'
import { CDN_PATH } from 'src/constants/global'
import styles from 'src/components/Lmn/Lmn.scss'
import mixpanel from 'mixpanel-browser'
import LmnPreliminaryAssessmentCheckbox from './LmnPreliminaryAssessmentCheckbox'

// eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
interface SelectedConditionsProps {
  [key: string]: boolean
}

const LmnPreliminaryAssessment = (): JSX.Element => {
  const { palette } = useTheme()
  const { lmnContextService } = useContext(LmnContext)
  const { send } = lmnContextService

  const lmnConditions = useSelector(lmnContextService, getLmnConditions)
  const lmnContraindications = useSelector(lmnContextService, getLmnContraindications)
  const { diagnosis } = useSelector(lmnContextService, getCustomerInfo)
  const [selectedConditions, setSelectedConditions] = useState<SelectedConditionsProps>({})
  const [
    selectedContraindications,
    setSelectedContraindications,
  ] = useState<SelectedConditionsProps>({})
  const [noContraindications, setNoContraindications] = useState<boolean>(false)
  const [assessmentError, setAssessmentError] = useState(false)
  const [assessmentErrorMessage, setAssessmentErrorMessage] = useState('')
  const [contraindicationsError, setContraindicationsError] = useState(false)
  const [contraindicationsErrorMessage, setContraindicationsErrorMessage] = useState('')
  const [termsError, setTermsError] = useState(false)
  const [termsErrorMessage, setTermsErrorMessage] = useState('')
  const [selfAttestationChecked, setSelfAttestationChecked] = useState(false)

  const handleSelfAttestationChecked = (
    _event: SyntheticEvent<Element, Event>,
    value: boolean,
  ): void => {
    setSelfAttestationChecked(value)
  }

  const diagnosisInvalid = Object.values(selectedConditions).every((value) => !value)
  const contraindicationsValid = Object.values(selectedContraindications).every((value) => !value)

  const handleNextStepClicked = (): void => {
    // Only allowing them to move forward if at least 1 reason is selected
    if (diagnosisInvalid) {
      setAssessmentErrorMessage('Please select at least one reason to continue.')
      setAssessmentError(true)
      return
    }
    if (assessmentError) {
      setAssessmentError(false)
      setAssessmentErrorMessage('')
    }

    if (lmnContraindications.length > 0) {
      if (!contraindicationsValid) {
        send({
          type: 'SEND_NOT_QUALIFIED',
          data: {
            lmnNotQualified: true,
          },
        })
        setContraindicationsErrorMessage('We\'re sorry. Based on your answers, we can’t provide a Letter of Medical Necessity at this time. You can still complete your purchase with another form of payment.')
        setContraindicationsError(true)
        return
      }
      if (!noContraindications) {
        setContraindicationsErrorMessage('Please select at least one reason to continue.')
        setContraindicationsError(true)
        return
      }
      if (contraindicationsError) {
        setContraindicationsError(false)
        setContraindicationsErrorMessage('')
      }
    }

    if (!selfAttestationChecked) {
      setTermsErrorMessage('Please confirm by reading and checking the corresponding box above.')
      setTermsError(true)
      return
    }
    setTermsError(false)
    setTermsErrorMessage('')

    const currentConditions = Object.keys(selectedConditions)
      .filter((key) => selectedConditions[key]).join('*')

    send({
      type: 'SET_DIAGNOSIS',
      data: {
        diagnosis: currentConditions,
      },
    })

    const selectedConditionsKeys = Object.keys(selectedConditions)

    const currentSymptoms = selectedConditionsKeys.reduce((acc, currentValue, idx) => {
      if (!selectedConditions[currentValue]) return acc

      if (idx === selectedConditionsKeys.length - 1) {
        return `${acc} and ${currentValue}`
      }

      return `${acc}, ${currentValue}`
    }).toLowerCase()

    send({
      type: 'SET_MEDICAL_PROFILE_INFO',
      data: {
        Q1: `Can you tell us a bit more about your ${currentSymptoms} symptoms?`,
      },
    })
    mixpanel.track('completed_lmn_preliminary_assessment')
  }

  useEffect(() => {
    mixpanel.track('view_lmn_preliminary_assessment')
    // Repopulating info if they go back to this screen
    if (diagnosis !== '') {
      const conditions = diagnosis.split('*')
      const newSelectedConditions: SelectedConditionsProps = {}
      conditions.forEach((condition) => {
        newSelectedConditions[condition] = true
      })
      setSelectedConditions(newSelectedConditions)
      setSelfAttestationChecked(true)
    }
    // only running this once on component load w/ empty array
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  const handleConditionChanged = (value: boolean, condition: string): void => {
    const newSelectedConditions = {
      ...selectedConditions,
      [condition]: value,
    }
    setSelectedConditions(newSelectedConditions)
  }

  const handleContraindicationsChanged = (value: boolean, contraindication: string): void => {
    const newSelectedContraindications = {
      ...selectedContraindications,
      [contraindication]: value,
    }
    setSelectedContraindications(newSelectedContraindications)
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="10px"
    >
      <Box>
        <Typography variant="body1Bold">
          Preliminary Assessment
        </Typography>
        <Typography variant="body3" color="gray1.main">
          Your purchase may be eligible for payment with HSA/FSA if used to address a
          medical condition. To see if you qualify, tell us more about your symptoms.
        </Typography>
      </Box>
      <Divider sx={{ marginY: '8px' }} />
      {lmnContraindications.length > 0 ? (
        <Box
          display="flex"
          gap="16px"
          alignItems="center"
        >
          <img src={`${CDN_PATH}/images/step-one.svg`} alt="Step 1" />
          <Typography variant="body2Bold">
            Intended Use
          </Typography>
        </Box>
      ) : null}
      <Box>
        <Typography
          variant="body3"
          marginBottom="4px"
        >
          Are you experiencing any of the following
          <b style={{ paddingLeft: '4px' }}>symptoms</b>
          ?
        </Typography>
        <FormControl>
          <Box display="flex">
            <Box display="flex" flexDirection="column" flex="1">
              {lmnConditions.map((condition) => (
                <LmnPreliminaryAssessmentCheckbox
                  key={condition}
                  condition={condition}
                  handleCheckboxChanged={handleConditionChanged}
                />
              ))}
            </Box>
          </Box>
        </FormControl>
      </Box>
      {assessmentError ? (
        <Typography variant="body3" color="error.main">
          {assessmentErrorMessage}
        </Typography>
      ) : null}
      {lmnContraindications.length > 0 ? (
        <>
          <Divider sx={{ marginY: '8px' }} />
          <Box
            display="flex"
            gap="16px"
            alignItems="center"
          >
            <img src={`${CDN_PATH}/images/step-two.svg`} alt="Step 2" />
            <Typography variant="body2Bold">
              Unsupported Medical Conditions
            </Typography>
          </Box>
          <Box>
            <Typography variant="body3" marginBottom="4px">
              This product may not be recommended for individuals who experience certain
              conditions. Have you previously been diagnosed with or treated for any
              of the following conditions?
            </Typography>
            <FormControl>
              <Box display="flex">
                <Box display="flex" flexDirection="column" flex="1">
                  {lmnContraindications.map((contraindication) => (
                    <LmnPreliminaryAssessmentCheckbox
                      key={contraindication}
                      condition={contraindication}
                      handleCheckboxChanged={handleContraindicationsChanged}
                    />
                  ))}
                  <FormControlLabel
                    sx={{ marginRight: '0px !important', marginBottom: '0px !important' }}
                    control={(
                      <Checkbox
                        size="small"
                        checked={noContraindications}
                        onChange={(
                          _event: SyntheticEvent<Element, Event>,
                          value: boolean,
                        ) => setNoContraindications(value)}
                      />
                    )}
                    label={<Typography variant="body3">I do not have any of the listed conditions</Typography>}
                  />
                </Box>
              </Box>
            </FormControl>
          </Box>
          {contraindicationsError ? (
            <Typography variant="body3" color="error.main">
              {contraindicationsErrorMessage}
            </Typography>
          ) : null}
        </>
      ) : null}
      <Divider sx={{ marginY: '8px' }} />
      <FormControl
        error={termsError}
        component="fieldset"
        variant="standard"
      >
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={selfAttestationChecked}
              onChange={handleSelfAttestationChecked}
              sx={{
                ...((termsError && !selfAttestationChecked) && { color: palette.error.main }),
              }}
            />
          )}
          label={(
            <Typography variant="body3">
              By checking this box, I attest that I wish to address the symptom(s)
              selected above. But for the symptom(s), I would not make this purchase.
              I am not purchasing this product for wellness or cosmetic purposes.
            </Typography>
          )}
        />
        {termsError ? (
          <FormHelperText
            sx={{
              fontSize: '14px',
              marginLeft: 0,
              marginRight: 0,
            }}
          >
            {termsErrorMessage}
          </FormHelperText>
        ) : null}
      </FormControl>
      <Button
        className={styles.lmnButton}
        color="primary"
        fullWidth
        size="large"
        variant="contained"
        onClick={handleNextStepClicked}
      >
        <Typography variant="body2Bold">
          Continue
        </Typography>
      </Button>
    </Box>
  )
}

export default LmnPreliminaryAssessment
